import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const STATS = [
  { value: 'R2', label: 'Certified buyers only' },
  { value: 'Low', label: 'Fees, no upfront cost' },
  { value: '3min', label: 'Anti-snipe protection' },
  { value: '24/7', label: 'Automated auctions' },
];

const HOW = [
  {
    num: '01',
    title: 'Create your listing',
    desc: 'Upload photos and a short video. Set your starting price and auction duration. Takes under 10 minutes.',
    icon: '📋',
  },
  {
    num: '02',
    title: 'Buyers compete',
    desc: 'Verified R2-certified buyers place competing offers. Each bid must beat the last — prices only go up.',
    icon: '⚡',
  },
  {
    num: '03',
    title: 'Auction closes automatically',
    desc: 'When the timer ends, the highest bidder wins. Anti-snipe protection extends the auction if bids come in the last 3 minutes.',
    icon: '🏁',
  },
  {
    num: '04',
    title: 'Confirm payment & ship',
    desc: 'The buyer has 3 days to pay. Once you confirm receipt, provide tracking info and the deal is done.',
    icon: '✅',
  },
];

const WHY = [
  {
    icon: '🔐',
    title: 'R2-Certified Buyers Only',
    desc: 'Every buyer on the platform must hold valid R2 certification — verified by our admin team before they place a single bid. No grey market. No compliance risk.',
  },
  {
    icon: '📈',
    title: 'Competitive Bidding = Better Prices',
    desc: 'Stop accepting the first offer you get. When multiple certified buyers compete for your inventory, the price naturally rises to true market value.',
  },
  {
    icon: '🕵️',
    title: 'Full Anonymity During Auction',
    desc: 'Buyers see "Seller" — not your company name. You see "Buyer 1, 2, 3" — not their identity. Privacy protects both parties until the deal is done.',
  },
  {
    icon: '⚙️',
    title: 'Fully Automated',
    desc: 'Set it and forget it. The auction runs, closes, and awards the winner automatically. No manual intervention needed on your end.',
  },
  {
    icon: '🛡️',
    title: 'Fair Auction Rules',
    desc: 'Offers are binding — no bait-and-switch from buyers. Anti-snipe protection ensures no last-second underhanded wins. The process is transparent and enforceable.',
  },
  {
    icon: '💰',
    title: 'Low Listing Fees',
    desc: 'We keep our fees low so you keep more of what you earn. No subscription, no upfront cost — you only pay a small fee when your auction closes successfully.',
  },
];

const CATEGORIES = [
  { icon: '📱', label: 'Cellphones' },
  { icon: '💻', label: 'Laptops' },
  { icon: '📟', label: 'Tablets' },
  { icon: '🖥️', label: 'Servers' },
  { icon: '💾', label: 'Hard Drives' },
  { icon: '🔋', label: 'DDR RAM' },
  { icon: '♻️', label: 'Other Electronics' },
];

export default function AboutPage() {
  const navigate = useNavigate();
  const heroRef = useRef(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e) => {
      const { left, top, width, height } = el.getBoundingClientRect();
      const x = ((e.clientX - left) / width - 0.5) * 20;
      const y = ((e.clientY - top) / height - 0.5) * 20;
      el.style.setProperty('--mx', `${x}px`);
      el.style.setProperty('--my', `${y}px`);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif", color: 'var(--text)', minHeight: '100vh' }}>

      {/* ─── HERO ─── */}
      <section ref={heroRef} style={{
        position: 'relative', overflow: 'hidden',
        padding: '100px 40px 120px', textAlign: 'center',
        background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(34,214,122,0.12) 0%, transparent 70%), var(--bg)',
      }}>
        {/* Animated grid */}
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.04,
          backgroundImage: 'linear-gradient(var(--accent) 1px, transparent 1px), linear-gradient(90deg, var(--accent) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
          transform: 'translate(calc(var(--mx, 0px) * 0.3), calc(var(--my, 0px) * 0.3))',
          transition: 'transform 0.1s ease',
        }} />

        <div style={{ position: 'relative', maxWidth: 800, margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 28,
            padding: '6px 16px', borderRadius: 100,
            background: 'rgba(34,214,122,0.1)', border: '1px solid rgba(34,214,122,0.25)',
          }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', animation: 'pulse 2s infinite' }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', letterSpacing: '0.08em' }}>NOW ACCEPTING SELLERS</span>
          </div>

          <h1 style={{
            fontSize: 'clamp(40px, 7vw, 80px)', fontWeight: 800, lineHeight: 1.05,
            letterSpacing: '-0.03em', margin: '0 0 24px',
            background: 'linear-gradient(135deg, var(--text) 0%, var(--accent) 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
            The auction marketplace<br />built for R2 recyclers.
          </h1>

          <p style={{
            fontSize: 20, color: 'var(--text2)', lineHeight: 1.7, maxWidth: 580,
            margin: '0 auto 40px', fontWeight: 400,
          }}>
            Stop accepting the first offer you get. List your recycled electronics inventory on R2 Markets and let <strong style={{ color: 'var(--text)' }}>verified R2-certified buyers compete</strong> for it.
          </p>

          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/register')} style={{
              padding: '16px 36px', borderRadius: 12, border: 'none', cursor: 'pointer',
              background: 'var(--accent)', color: '#000', fontSize: 16, fontWeight: 700,
              fontFamily: 'inherit', letterSpacing: '-0.01em',
              boxShadow: '0 0 40px rgba(34,214,122,0.3)',
              transition: 'all 0.2s',
            }}
              onMouseEnter={e => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 8px 40px rgba(34,214,122,0.4)'; }}
              onMouseLeave={e => { e.target.style.transform = 'none'; e.target.style.boxShadow = '0 0 40px rgba(34,214,122,0.3)'; }}
            >
              Start Selling Today →
            </button>
            <button onClick={() => navigate('/')} style={{
              padding: '16px 36px', borderRadius: 12, cursor: 'pointer', fontSize: 16, fontWeight: 600,
              background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)',
              fontFamily: 'inherit', transition: 'all 0.2s',
            }}
              onMouseEnter={e => { e.target.style.borderColor = 'var(--accent)'; e.target.style.color = 'var(--accent)'; }}
              onMouseLeave={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.color = 'var(--text2)'; }}
            >
              Browse Listings
            </button>
          </div>
        </div>
      </section>

      {/* ─── STATS BAR ─── */}
      <section style={{
        borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)',
        background: 'var(--surface)', padding: '32px 40px',
      }}>
        <div style={{
          maxWidth: 900, margin: '0 auto',
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 32,
        }}>
          {STATS.map(s => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 36, fontWeight: 800, color: 'var(--accent)', letterSpacing: '-0.03em', fontFamily: 'var(--mono)' }}>{s.value}</div>
              <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── WHY SELL ─── */}
      <section style={{ padding: '100px 40px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.15em', marginBottom: 16 }}>WHY SELL ON R2 MARKETS</div>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 800, letterSpacing: '-0.02em', margin: 0 }}>
            Built around your needs<br />as a certified recycler.
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 }}>
          {WHY.map((w, i) => (
            <div key={w.title} style={{
              padding: 28, borderRadius: 16, border: '1px solid var(--border)',
              background: 'var(--surface)', transition: 'all 0.25s',
              animation: `fadeUp 0.5s ease both`, animationDelay: `${i * 80}ms`,
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(34,214,122,0.4)'; e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.2)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{ fontSize: 32, marginBottom: 16 }}>{w.icon}</div>
              <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 10, letterSpacing: '-0.01em' }}>{w.title}</div>
              <div style={{ fontSize: 14, color: 'var(--text3)', lineHeight: 1.7 }}>{w.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section style={{
        padding: '100px 40px',
        background: 'radial-gradient(ellipse 80% 60% at 50% 100%, rgba(34,214,122,0.06) 0%, transparent 70%), var(--bg2, var(--bg))',
        borderTop: '1px solid var(--border)',
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.15em', marginBottom: 16 }}>HOW IT WORKS</div>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 800, letterSpacing: '-0.02em', margin: 0 }}>
              From listing to payment<br />in four steps.
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {HOW.map((step, i) => (
              <div key={step.num} style={{
                display: 'grid', gridTemplateColumns: '80px 1fr', gap: 32, padding: '40px 0',
                borderBottom: i < HOW.length - 1 ? '1px solid var(--border)' : 'none',
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: 16, background: 'rgba(34,214,122,0.1)',
                    border: '1px solid rgba(34,214,122,0.2)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 24,
                  }}>{step.icon}</div>
                </div>
                <div style={{ paddingTop: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.12em', marginBottom: 8 }}>STEP {step.num}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em', marginBottom: 10 }}>{step.title}</div>
                  <div style={{ fontSize: 15, color: 'var(--text3)', lineHeight: 1.7 }}>{step.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CATEGORIES ─── */}
      <section style={{ padding: '80px 40px', maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.15em', marginBottom: 16 }}>WHAT YOU CAN SELL</div>
        <h2 style={{ fontSize: 'clamp(24px, 3vw, 40px)', fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 48px' }}>
          All major categories accepted.
        </h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center' }}>
          {CATEGORIES.map(c => (
            <div key={c.label} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '14px 24px',
              borderRadius: 12, border: '1px solid var(--border)', background: 'var(--surface)',
              fontSize: 15, fontWeight: 500, transition: 'all 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(34,214,122,0.4)'; e.currentTarget.style.background = 'rgba(34,214,122,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--surface)'; }}
            >
              <span style={{ fontSize: 22 }}>{c.icon}</span> {c.label}
            </div>
          ))}
        </div>
        <p style={{ fontSize: 14, color: 'var(--text3)', marginTop: 24 }}>
          Sell by unit (pieces) or by weight (lbs). Grade A through X accepted.
        </p>
      </section>

      {/* ─── CTA ─── */}
      <section style={{
        padding: '100px 40px', textAlign: 'center',
        borderTop: '1px solid var(--border)',
        background: 'radial-gradient(ellipse 60% 80% at 50% 50%, rgba(34,214,122,0.08) 0%, transparent 70%)',
      }}>
        <h2 style={{ fontSize: 'clamp(32px, 5vw, 64px)', fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 20px' }}>
          Ready to get better prices<br />for your inventory?
        </h2>
        <p style={{ fontSize: 18, color: 'var(--text3)', margin: '0 auto 48px', maxWidth: 480, lineHeight: 1.6 }}>
          Join R2 Markets today. Create your first listing in under 10 minutes. Low fees, no upfront cost.
        </p>
        <button onClick={() => navigate('/register')} style={{
          padding: '20px 52px', borderRadius: 14, border: 'none', cursor: 'pointer',
          background: 'var(--accent)', color: '#000', fontSize: 18, fontWeight: 800,
          fontFamily: 'inherit', letterSpacing: '-0.01em',
          boxShadow: '0 0 60px rgba(34,214,122,0.35)',
          transition: 'all 0.2s',
        }}
          onMouseEnter={e => { e.target.style.transform = 'translateY(-3px) scale(1.02)'; e.target.style.boxShadow = '0 12px 60px rgba(34,214,122,0.45)'; }}
          onMouseLeave={e => { e.target.style.transform = 'none'; e.target.style.boxShadow = '0 0 60px rgba(34,214,122,0.35)'; }}
        >
          Start Selling Today →
        </button>
        <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 20 }}>
          Already have an account? <span onClick={() => navigate('/login')} style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 500 }}>Log in</span>
        </div>
      </section>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
